import React, { useRef, useEffect } from 'react';

import { useArtistModalStore } from '../../stores/artistModalStore';
import { useFormModalStore } from '../../stores/formStore';

import styles from './AboutFundPage.module.less';
import { useAboutFund } from '../../hooks/useAboutFund';
import { useTranslatedContent } from '../../hooks/useTranslatedContent';
import { setupLinkHandlers } from '../../helpers/linkHandler';
import { Footer } from '../../components/Footer/Footer';
import { useTranslation } from 'react-i18next';
import { SocialNetwork } from '../../types/about_fund';
import { useTheme } from '../../stores/themeStore';
import { useLocation } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'http://0.0.0.0:8055';

export type ScrollBehavior = 'auto' | 'smooth';
export type ScrollBlock = 'start' | 'center' | 'end' | 'nearest';
export type ScrollInline = 'start' | 'center' | 'end' | 'nearest';

export interface ScrollOptions {
  behavior?: ScrollBehavior;
  block?: ScrollBlock;
  inline?: ScrollInline;
  offset?: number;
}


export const AboutFundPage: React.FC = () => {
  const { data,  error } = useAboutFund();
  const { getTranslation, i18n } = useTranslatedContent();
  const openArtistModal = useArtistModalStore((state) => state.openModal);
  const openFormModal = useFormModalStore((state) => state.openModal);
  const { t } = useTranslation();



  const { theme } = useTheme();

  const pageRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const scrollAttempts = useRef(0);
  const maxAttempts = 5;

  const scrollToElement = (elementId: string, options: ScrollOptions = {}) => {
    const element = document.getElementById(elementId);

    if (!element && scrollAttempts.current < maxAttempts) {
     
      scrollAttempts.current += 1;
      setTimeout(() => {
        scrollToElement(elementId, options);
      }, 300);
      return;
    }

    if (!element) {
      console.warn(
        `Елемент з ID "${elementId}" не знайдено після ${maxAttempts} спроб`
      );
      return;
    }

    const defaultOptions: Required<ScrollOptions> = {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
      offset: 0,
    };

    const scrollOptions: Required<ScrollOptions> = {
      ...defaultOptions,
      ...options,
    };

    Promise.all(
      Array.from(document.images)
        .filter((img) => !img.complete)
        .map(
          (img) =>
            new Promise((resolve) => {
              img.onload = img.onerror = resolve;
            })
        )
    ).then(() => {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = window.innerHeight / 6;

      let scrollPosition;

      if (scrollOptions.block === 'center') {
        scrollPosition = absoluteElementTop - middle + elementRect.height / 6;
      } else {
        scrollPosition = absoluteElementTop;
      }

      scrollPosition -= scrollOptions.offset;

      window.scrollTo({
        top: scrollPosition,
        behavior: scrollOptions.behavior,
      });
    });
  };

  useEffect(() => {
  
    scrollAttempts.current = 0;

    const hash = location.hash.slice(1);
    if (hash) {
     
      const initialDelay = location.key ? 0 : 500;
      setTimeout(() => {
        scrollToElement(hash, {
          behavior: 'smooth',
          block: 'center',
          offset: 60,
        });
      }, initialDelay);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!pageRef.current) return;

    const cleanup = setupLinkHandlers(pageRef.current, {
      openArtistModal,
      openFormModal,
      theme,
    });

    return cleanup;
  }, [openArtistModal, openFormModal, data]);



  if (error) {
    return <div className={styles.aboutFund__error}>Error loading data</div>;
  }

  if (!data?.[0]) {
    return <div className={styles.aboutFund__empty}>No data available</div>;
  }

  const aboutFundData = data[0];
  const description = getTranslation(aboutFundData, 'description') || '';

  const actualProjects =
    aboutFundData.projects?.filter(
      (project) => !project?.allContent_id?.archive
    ) || [];
 
  

  const archiveProjects =
    aboutFundData.projects?.filter(
      (project) => project?.allContent_id?.archive === true
    ) || [];



  const reports = aboutFundData.reports || [];

  const renderProjectsList = (projects: typeof actualProjects) => {
    return (
      <div className={styles.aboutFund__projectsList}>
        <table className={styles.aboutFund__table}>
          <thead>
            <tr>
              <th>{t('Project')}</th>
              <th>{t('Name')}</th>
              <th>{t('Who')}</th>
              <th>{t('Year')}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const title = project.allContent_id?.translations[0].title ? 
                getTranslation(project.allContent_id, 'title') : '';
              const category =  project.allContent_id?.category ?
                getTranslation(
                  project.allContent_id.category,
                  'category_name'
                ) : null;
              const members =
                project.allContent_id?.members
                  ?.map((member) => {
                    const translations = member.artist_info_id.translations;
                    const artistName =
                      translations?.length === 2
                        ? translations[i18n.language === 'en' ? 1 : 0]
                            .artist_name
                        : translations?.[0]?.artist_name || '';
                    return artistName;
                  })
                  .filter(Boolean)
                  .join(', ') || '';
              const year = project.allContent_id?.years?.[0]?.years_id?.year;


              return (
                <tr 
                className={styles.test}
                  key={project.allContent_id?.slug}
                  onClick={() => {
                    window.location.href = `/${category ? (project?.allContent_id?.category as unknown as { slug: string }).slug : 'category'}/${project.allContent_id.slug}`;
                                        

                  }}
                >
                  <td className={styles.aboutFund__item}>{category }</td>
                  <td className={styles.aboutFund__item}>{title}</td>
                  <td className={styles.aboutFund__item}>{members}</td>
                  <td className={styles.aboutFund__item}>{year}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div ref={pageRef} className={styles.aboutFund}>
      <div
        className={styles.aboutFund__description}
        dangerouslySetInnerHTML={{ __html: description }}
      />

      <div className={styles.aboutFund__section}>
        <h2 className={styles.aboutFund__sectionTitle}>{t('Actual')}</h2>
        {renderProjectsList(actualProjects)}
      </div>

      {archiveProjects.length > 0 && (
        <div className={styles.aboutFund__section} id="archive">
          <h2 className={styles.aboutFund__sectionTitle}>{t('archive')}</h2>
          {renderProjectsList(archiveProjects)}
        </div>
      )}

      {reports.length > 0 && (
        <div className={styles.aboutFund__sectionReports}>
          <h2 className={styles.aboutFund__reportsTitle}>{t('Reports')}</h2>
          <div className={styles.aboutFund__reportsList}>
            {reports.map((report) => {
              const title = getTranslation(report, 'title') || '';
              return (
                <div className={styles.aboutFund__reportLink} key={report.id}>
                  {title}
                  <a
                    href={`${API_URL}/assets/${report.file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('View')}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className={styles.aboutFund__info} id="contacts">
        <div className={styles.aboutFund__info__contacts}>
          <h3 className={styles.aboutFund__info__title}>
            {getTranslation(aboutFundData, 'contacts_title')}
          </h3>

          <ul className={styles.aboutFund__info__list}>
            {aboutFundData?.social_network?.map((social, index) => (
              <li key={index}>
                <a
                  href={social.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.aboutFund__info__contactsItem}
                >
                  {social.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.aboutFund__info__support}>
          <h3 className={styles.aboutFund__info__title}>
            {getTranslation(aboutFundData, 'support_title')}
          </h3>
          <ul className={styles.aboutFund__info__list}>
            {getTranslation(aboutFundData, 'support')?.map(
              (item: SocialNetwork, index: number) => (
                <li
                  className={styles.aboutFund__info__supportItems}
                  key={index}
                >
                  <div className={styles.aboutFund__info__supportItem}>
                    <div className={styles.aboutFund__info__supportTitle}>
                      {item.title}
                    </div>
                    <div className={styles.aboutFund__info__supportValue}>
                      {item.value}
                    </div>
                  </div>
                </li>
              )
            )}
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};
